import { createBrowserRouter, Navigate } from "react-router-dom";

import { ReviewRedirect } from "./routes/ReviewRedirect";
import { LearningAgentsRedirect } from "./routes/LearningAgentsRedirect";
import { RouteErrorElement } from "./components/RouteErrorBoundary";

import { LoginRedirect } from "./auth";
import { isDemoMode } from "./demo/demoMode";
import { RootLayout } from "./layouts/RootLayout";
import { GlobalDataHistoryRoute } from "./routes/GlobalDataHistoryRoute";
import { GlobalDataRoute } from "./routes/GlobalDataRoute";

import { CostsRoute } from "./routes/CostsRoute";
import { DiagnosticsAccessRoute } from "./routes/DiagnosticsAccessRoute";
import { DashboardRoute } from "./routes/DashboardRoute";
import { MyActionsRoute } from "./routes/MyActionsRoute";
import { GoogleAuthCallbackRoute } from "./routes/GoogleAuthCallbackRoute";
import { AmazonEusdrQuestionsRoute } from "./routes/AmazonEusdrQuestionsRoute";
import { AmazonNeededRoute } from "./routes/AmazonNeededRoute";
import {
  AmazonOverviewLayout,
  PortfolioStatusPage,
  ProcessOverviewPage,
  SiteDetailsPage,
  ToFirstPage,
} from "./routes/AmazonOverviewRoute";
import { GridTableRoute } from "./routes/GridTableRoute";
import { IntegrationsRoute } from "./routes/IntegrationsRoute";
import { NewProjectRoute } from "./routes/NewProjectRoute";
import { OperationsRoute } from "./routes/OperationsRoute";
import {
  ProjectAgentStateRoute,
  ProjectAgentStateTab,
} from "./routes/ProjectAgentStateTab";
import { StatefulRunRedirectRoute } from "./routes/StatefulRunRedirectRoute";
import { ProjectFilesRoute } from "./routes/ProjectFilesRoute";
import { RegisterRoute } from "./routes/RegisterRoute";
import { ProjectHubLayout } from "./routes/ProjectHubLayout";
import { ProjectSpecialistTab } from "./routes/ProjectSpecialistTab";
import { ProjectAllActionsTab } from "./routes/ProjectAllActionsTab";
import { ProjectDebugGraphTab } from "./routes/ProjectDebugGraphTab";
import { ProjectStatusTab } from "./routes/ProjectStatusTab";
import { ProjectProcurementTab } from "./routes/ProjectProcurementTab";
import { ProjectSettingsTab } from "./routes/ProjectSettingsTab";
import { ProjectSettingsHistoryRoute } from "./routes/ProjectSettingsHistoryRoute";
import {
  ProjectStatefulRunDetailRoute,
  ProjectStatefulRunsTab,
} from "./routes/ProjectStatefulRunsTab";
import { ProjectAttributesTab } from "./routes/ProjectAttributesTab";
import { ProjectScheduledRunsTab } from "./routes/ProjectScheduledRunsTab";
import { AgentPortfolioTab } from "./routes/AgentPortfolioTab";
import { AgentPromptTab } from "./routes/AgentPromptTab";
import { AgentConversationsTab, AgentScheduledRunsTab } from "./routes/AgentRunsTab";
import { AgentStateTab } from "./routes/AgentStateTab";
import {
  AgentScheduledRunDetailRoute,
  ProjectScheduledRunDetailRoute,
} from "./routes/AgentScheduledRunDetailRoute";
import {
  ProjectStatusHistoryRoute,
  ProjectStatusVersionRoute,
} from "./routes/ProjectStatusHistory";
import { TeamSettingsTab } from "./routes/TeamSettingsTab";
import { StatefulAgentsLayout } from "./routes/StatefulAgentsLayout";
import { ProjectsRoute } from "./routes/ProjectsRoute";
import { HOME_PATH } from "./utils/homePath";


// Mirrors admin-app's createBrowserRouter pattern. One layout
// (AppLayout) renders the sidebar + topbar + outlet; routes render
// inside the outlet.
export const router = createBrowserRouter([
  // Kinde post-login redirect target. Sits at the top level (no
  // AppLayout) so the redirect lands cleanly before the layout +
  // its API hooks fire.
  { path: "/login-redirect", element: <LoginRedirect /> },
  // Google OAuth callback. Bare route (no AppLayout) so the layout's
  // hooks don't fire before we've completed the token exchange.
  { path: "/google/callback", element: <GoogleAuthCallbackRoute /> },
  {
    path: "/",
    element: <RootLayout />,
    // Under the LAYOUT, so a page that throws while rendering keeps the nav
    // rather than react-router replacing the whole document with its error.
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: <Navigate to={HOME_PATH} replace /> },
      { path: "operations", element: <OperationsRoute /> },
      // Costs are hidden entirely in demo mode (see docs/Y2026-Q2/design-demo-mode.md):
      // bounce /costs home rather than rendering any spend data.
      {
        path: "costs",
        element: isDemoMode() ? (
          <Navigate to={HOME_PATH} replace />
        ) : (
          <CostsRoute />
        ),
      },
      { path: "integrations", element: <IntegrationsRoute /> },
      { path: "global-data", element: <GlobalDataRoute /> },
      { path: "global-data/history", element: <GlobalDataHistoryRoute /> },
      { path: "global-data/registers/:documentId", element: <RegisterRoute /> },
      // The agents' global surface
      // (docs/Y2026-Q3/design-project-agent-team-runs.md section 2.3): the project
      // team under one roof.
      // A run known only by its id, opened on whichever page shows its kind. Outside the
      // team layout: it is a lookup rather than a tab, and the layout sends a path that is
      // not one of its tabs to its landing tab.
      { path: "agents/runs/:runId", element: <StatefulRunRedirectRoute /> },
      {
        path: "agents",
        element: <StatefulAgentsLayout />,
        // The layout sends a bare /agents to the team's landing tab.
        children: [{ index: true, element: null }],
      },
      {
        path: "agents/project-agents",
        element: <StatefulAgentsLayout />,
        children: [
          { index: true, element: null },
          { path: "outputs/:agent/portfolio", element: <AgentPortfolioTab /> },
          // Bookmarks to the retired daily digest page.
          {
            path: "outputs/:agent/digest",
            element: <Navigate to="../portfolio" relative="path" replace />,
          },
          // The team's daily run is one run, so its runs are the team's.
          { path: "scheduled", element: <AgentScheduledRunsTab /> },
          { path: "scheduled/:runId", element: <AgentScheduledRunDetailRoute projectId={null} /> },
          { path: "conversations", element: <AgentConversationsTab /> },
          { path: "settings", element: <TeamSettingsTab /> },
          // Same component as the scheduled run - a run is a run.
          { path: "conversations/:runId", element: <AgentScheduledRunDetailRoute projectId={null} /> },
          { path: ":agent/state", element: <AgentStateTab /> },
          // The one document with no project to hang it off, so it hangs off
          // the agent. Static path, no id: an agent has exactly one.
          { path: ":agent/prompt", element: <AgentPromptTab /> },
          // The layout sends anything else to the landing tab.
          { path: "*", element: null },
        ],
      },
      {
        path: "agents/:agent",
        element: <StatefulAgentsLayout />,
        children: [
          // An agent's old paths (/agents/grid/portfolio, ...): the layout
          // forwards every one to its new home under the team.
          { index: true, element: null },
          { path: "*", element: null },
        ],
      },
      { path: "learning-agents/*", element: <LearningAgentsRedirect /> },
      { path: "new", element: <NewProjectRoute /> },
      { path: "dashboard", element: <DashboardRoute /> },
      // Reached by URL only, deliberately left out of the nav.
      { path: "grid", element: <GridTableRoute /> },
      // Reached by URL only: the link a Claude Code session prints when it asks for access.
      { path: "diagnostics-access", element: <DiagnosticsAccessRoute /> },
      // Reached by URL only, like /grid.
      {
        path: "amazon-overview",
        element: <AmazonOverviewLayout />,
        children: [
          { index: true, element: <ToFirstPage /> },
          { path: "needed", element: <AmazonNeededRoute /> },
          { path: "eusdr", element: <AmazonEusdrQuestionsRoute /> },
          { path: "portfolio", element: <PortfolioStatusPage /> },
          { path: "sites", element: <SiteDetailsPage /> },
          { path: "sites/:code", element: <SiteDetailsPage /> },
          { path: "process", element: <ProcessOverviewPage /> },
          { path: "*", element: <ToFirstPage /> },
        ],
      },
      { path: "my-actions", element: <MyActionsRoute /> },
      { path: "projects", element: <ProjectsRoute /> },
      {
        path: "projects/:projectId",
        element: <ProjectHubLayout />,
        children: [
          { index: true, element: <Navigate to="status" replace /> },
          // Where the project stands, one card per agent working it, and each
          // row-keeping specialist's own file one click through.
          { path: "status", element: <ProjectStatusTab /> },
          // Every specialist's actions on the project in one table.
          { path: "actions", element: <ProjectAllActionsTab /> },
          // Every specialist's own page, those with a status row and those without.
          { path: "status/:agent", element: <ProjectSpecialistTab /> },
          // Links to the project's write-up land on the Overview; earlier write-ups are
          // on their scheduled runs (design 7.3).
          { path: "status/briefing", element: <Navigate to="../status" replace /> },
          // Reviews are conversations now, listed with the project's others.
          {
            path: "status/reviews",
            element: <Navigate to="../learning-agents/conversations" replace />,
          },
          { path: "status/reviews/:runId", element: <ReviewRedirect /> },
          { path: "status/attributes", element: <ProjectAttributesTab /> },
          { path: "status/graph", element: <ProjectDebugGraphTab /> },
          { path: "status-history", element: <ProjectStatusHistoryRoute /> },
          { path: "status-history/:version", element: <ProjectStatusVersionRoute /> },
          { path: "state-history", element: <Navigate to="../status-history" replace /> },
          // Where these lived before they moved under the Overview they belong to.
          { path: "grid", element: <Navigate to="../status/grid" replace /> },
          { path: "legal", element: <Navigate to="../status/legal" replace /> },
          // The retired legacy fleet's Overview and Project data pages.
          { path: "overview", element: <Navigate to="../status" replace /> },
          { path: "tracker", element: <Navigate to="../status" replace /> },
          // Per-agent durable state documents: current version, full
          // revision history, and a diff between any two revisions.
          // Kept as a redirect, not deleted: it is a URL that has been linked
          // to, including from the agent-side State table.
          {
            path: "agent-state",
            element: <Navigate to="../learning-agents/state" replace />,
          },
          // Operator conversations with the stateful suite (Manager + Grid),
          // and the accept / reject gate for what they propose.
          {
            path: "learning-agents",
            element: <Navigate to="conversations" replace />,
          },
          {
            path: "learning-agents/conversations",
            element: <ProjectStatefulRunsTab />,
          },
          // Static before the :runId sibling below - react-router ranks a
          // static segment higher, but declaring it first says so plainly.
          {
            path: "learning-agents/scheduled",
            element: <ProjectScheduledRunsTab />,
          },
          {
            path: "learning-agents/scheduled/:runId",
            element: <ProjectScheduledRunDetailRoute />,
          },
          // Moved under Status, beside the briefing its values feed.
          {
            path: "learning-agents/attributes",
            element: <Navigate to="../status/attributes" replace />,
          },
          { path: "learning-agents/state", element: <ProjectAgentStateTab /> },
          {
            path: "learning-agents/state/:agent",
            element: <ProjectAgentStateRoute />,
          },
          // Deep link to one learning-agent run: linkable, refreshable, and it
          // keeps the hub's header + tabs.
          {
            path: "learning-agents/conversations/:runId",
            element: <ProjectStatefulRunDetailRoute />,
          },
          { path: "files", element: <ProjectFilesRoute /> },
          { path: "files/register/:documentId", element: <RegisterRoute /> },
          { path: "procurement", element: <ProjectProcurementTab /> },
          { path: "settings", element: <ProjectSettingsTab /> },
          { path: "settings/history", element: <ProjectSettingsHistoryRoute /> },
        ],
      },
      {
        path: "*",
        element: <Navigate to={HOME_PATH} replace />,
      },
    ],
  },
]);
